import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Table, Accordion, AccordionItem, AccordionBody, AccordionHeader } from 'react-bootstrap';

const MODELS = [
  '12 mm (Yellow)',
  '8 mm (Blue)'
];

function groupByDate(data) {
  const grouped = {};
  data.forEach(item => {
    const date = item.Date || item.date || 'Unknown';
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(item);
  });
  return grouped;
}

function App() {
  const [model, setModel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState("0-0");

  useEffect(() => {
    const savedModel = localStorage.getItem("selectedModel");
    const savedStartDate = localStorage.getItem("startDate");
    const savedEndDate = localStorage.getItem("endDate");

    if (savedModel) setModel(savedModel);
    if (savedStartDate) setStartDate(savedStartDate);
    if (savedEndDate) setEndDate(savedEndDate);
  }, []);

  useEffect(() => {
    if (model) {
      localStorage.setItem("selectedModel", model);
      setActiveKey("0-0");
    } else {
      localStorage.removeItem("selectedModel");
    }
    if (startDate) {
      localStorage.setItem("startDate", startDate);
    } else {
      localStorage.removeItem("startDate");
    }
    if (endDate) {
      localStorage.setItem("endDate", endDate);
    } else {
      localStorage.removeItem("endDate");
    }
  }, [model, startDate, endDate]);


  const fetchData = () => {
    if (!model || !startDate || !endDate) return;
    setLoading(true);
    setError('');
    fetch(`${window.__ENV__.API_BASE_URL}/ReportSummary?ModelName=${encodeURIComponent(model)}&startdate=${startDate}&enddate=${endDate}`)
      .then(res => res.json())
      .then(data => {
        setReportData(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to fetch data');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [model, startDate, endDate]);

  const grouped = groupByDate(reportData);

  return (
    <Container className="my-4">
      <Card.Title className="mb-3">Inspection Summary</Card.Title>
      <Card className="mb-4">
        <Card.Body>
          <Form as={Row} className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Model</Form.Label>
                <Form.Select value={model} onChange={e => setModel(e.target.value)}>
                  <option value="">-- Select Model --</option>
                  {MODELS.map(m => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} max={endDate || ''} />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate || ''} />
              </Form.Group>
            </Col>
            {/* <Col xs={12} md={2} className="d-grid">
              <Button variant="primary" onClick={fetchData}>
                Fetch
              </Button>
            </Col> */}
          </Form>
        </Card.Body>
      </Card>
      {loading && <Spinner animation="border" />}
      {error && <div className="text-danger">{error}</div>}
      <Accordion activeKey={activeKey} onSelect={setActiveKey}>
        {Object.keys(grouped).map((date, idx) =>
          grouped[date].map((item, innerIdx) => {
            const eventKey = `${idx}-${innerIdx}`;

            const totalCount = item.results.reduce((sum, r) => sum + r.count, 0);
            const totalPass = item.results
              .filter(r => r.gapResult)
              .reduce((sum, r) => sum + r.count, 0);
            const totalFail = totalCount - totalPass;

            const passPercentage = totalCount ? ((totalPass / totalCount) * 100).toFixed(2) : 0;
            const failPercentage = totalCount ? ((totalFail / totalCount) * 100).toFixed(2) : 0;
            return (
              <Accordion.Item eventKey={eventKey} key={`${date}-${innerIdx}`}>
                <Accordion.Header>
                  <div className="d-flex flex-column">
                    <strong>
                      <span className="d-none d-lg-inline">Last Inspection : </span>
                      {new Date(item.lastInspectionDateTime).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      }).replace(",", "").toUpperCase()
                      }
                    </strong><br />
                    <div className="d-flex justify-content-around text-center small gap-2 py-1">
                      <div><strong>Total:</strong> {totalCount}</div>
                      <div><strong>Pass:</strong> {passPercentage}%</div>
                      <div><strong>Fail:</strong> {failPercentage}%</div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Table striped bordered hover size="sm" responsive>
                    <thead>
                      <tr>
                        <th>Gap Result</th>
                        <th>Count</th>
                        <th>(%)</th>
                        <th>Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.results.map((res, i) => (
                        <tr key={i}>
                          <td>{res.gapResult ? "Pass" : "Fail"}</td>
                          <td>{res.count}</td>
                          <td>{res.percentage}</td>
                          <td>{res.rangeInMicrons}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="mt-1 p-1 border rounded bg-light">
                    <Row>
                      <Col xs={12} md={4} className="mb-1 mb-md-0">
                        <div>
                          <strong>Total :</strong> {totalCount}
                        </div>
                      </Col>
                      <Col xs={12} md={4} className="mb-1 mb-md-0">
                        <div>
                          <strong>Pass :</strong> {passPercentage}%
                        </div>
                      </Col>
                      <Col xs={12} md={4}>
                        <div>
                          <strong>Fail :</strong> {failPercentage}%
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })
        )}
      </Accordion>
      {Object.keys(grouped).length > 0 && (
        <Card.Footer className="text-center mt-4">
          Visionware Technologies
        </Card.Footer>
      )}
    </Container>
  );
}

export default App;